import React, {
    ForwardedRef,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useState,
} from 'react';

import { Dimensions, Keyboard, Platform } from 'react-native';

import Modal from 'react-native-modal';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useDispatch } from 'react-redux';

import { useNetInfo } from '@react-native-community/netinfo';

import { setModalVisible } from 'store/user/actions';

import { RetryController } from 'services/opf-retry-mechanism';

import useKeyboardCloseHandler from 'utils/hooks/useKeyboardCloseHandler';
import usePrevious from 'utils/hooks/usePrevious';
import { lockToLandscape, lockToPortrait } from 'utils/orientation';
import { isTabletOrWeb, isWeb } from 'utils/responsiveness';

import { modalProps } from 'screens/PlayerModal/utils';

import NoInternetAlert from 'components/NoInternetAlert';
import showKeyboardByRef from 'components/PinDialog/utils/showKeyboardByRef';
import VitalErrorDialog from 'components/VitalErrorDialog';

import PinDialogBody from './components/PinDialogBody';
import { PinDialogStatus } from './hooks/getPinDialogText';
import styles from './styles';
import { PinDialogBodyRef, PinDialogProps, PinDialogViewProps } from './types';

export type PinDialogRef = {
    clear(): void;
    focus(): void;
    closeKeyboard(): void;
};

function PinDialogView({
    bodyRef,
    status,
    appState,
    haveToShowCloseButton,
    onSubmit,
    onBodyPress,
    onClose,
}: PinDialogViewProps) {
    return (
        <SafeAreaView style={styles.container}>
            <PinDialogBody
                ref={bodyRef}
                status={status}
                appState={appState}
                haveToShowCloseButton={haveToShowCloseButton}
                onSubmit={onSubmit}
                onBodyPress={onBodyPress}
                onClose={onClose}
            />
            <NoInternetAlert isModalNotification />
        </SafeAreaView>
    );
}

function PinDialog(
    {
        visible,
        modal = true,
        status,
        appState,
        haveToShowCloseButton,
        style,
        fromPlayer,
        onSubmit,
        onClose,
        onHide,
    }: PinDialogProps,
    ref: ForwardedRef<PinDialogRef>,
) {
    const dispatch = useDispatch();
    const prevAppState = usePrevious(appState);
    const prevPinStatus = usePrevious(status);
    const [keyboardVisible, setKeyboardVisible] = useState(false);
    const [keyboardWasShown, setKeyboardWasShown] = useState(false);
    const { isConnected } = useNetInfo();
    const bodyRef = React.useRef<PinDialogBodyRef | null>(null);
    const screenHeight = Dimensions.get('screen').height;

    useImperativeHandle(ref, () => ({
        clear() {
            bodyRef.current?.clear();
        },
        focus() {
            bodyRef.current?.focus();
        },
        closeKeyboard() {
            bodyRef.current?.blur();
        },
    }));

    const handleClosePinDialog = useCallback(() => {
        if (!RetryController.vitalDialogVisible) {
            bodyRef.current?.blur();
            if (!isTabletOrWeb && fromPlayer) {
                lockToLandscape();
            }
            onClose();
            dispatch(setModalVisible(false));
        }
    }, [onClose]);

    useEffect(() => {
        if (isWeb && visible) {
            window.history.pushState({}, '', null);
            window.addEventListener('popstate', handleClosePinDialog);
        }

        return () => {
            if (isWeb) {
                window.removeEventListener('popstate', handleClosePinDialog);
            }
        };
    }, [visible, handleClosePinDialog]);

    const handleShowKeyboard = useCallback(() => {
        if (status !== PinDialogStatus.BLOCKED && visible && isConnected) {
            setTimeout(() => {
                showKeyboardByRef(bodyRef, true);
            }, 100);
        }
    }, [status, visible, isConnected]);

    useEffect(() => {
        if (
            prevPinStatus === PinDialogStatus.BLOCKED &&
            status !== PinDialogStatus.BLOCKED
        ) {
            handleShowKeyboard();
        }
    }, [status]);

    useEffect(() => {
        if (!modal && !keyboardVisible) {
            setTimeout(() => {
                showKeyboardByRef(bodyRef, false);
            }, 100);
        }
    }, []);

    const onShow = useCallback(() => {
        if (!isTabletOrWeb && fromPlayer) {
            lockToPortrait();
        }

        if (Platform.OS === 'android') {
            dispatch(setModalVisible(true));

            handleShowKeyboard();
        }
    }, [handleShowKeyboard]);

    useEffect(() => {
        const showSubscription = Keyboard.addListener('keyboardDidShow', () => {
            setKeyboardWasShown(true);
            setKeyboardVisible(true);
        });

        const hideSubscription = Keyboard.addListener('keyboardDidHide', () => {
            setKeyboardVisible(false);
        });

        return () => {
            showSubscription.remove();
            hideSubscription.remove();
        };
    }, []);

    useEffect(() => {
        return () => {
            setKeyboardWasShown(false);
        };
    }, [visible]);

    const handleBodyPress = useCallback(() => {
        if (!keyboardVisible && status !== PinDialogStatus.BLOCKED) {
            showKeyboardByRef(bodyRef, keyboardWasShown);
        }
    }, [status, keyboardWasShown, keyboardVisible]);

    useEffect(() => {
        if (
            appState === 'active' &&
            prevAppState === 'background' &&
            status !== PinDialogStatus.BLOCKED
        ) {
            if (keyboardVisible) {
                // the keyboard can't be visible after returning from the 'background' state, the value of keyboardVisible is falsely true
                setKeyboardVisible(false);
            }

            showKeyboardByRef(bodyRef, keyboardWasShown);
        }
    }, [status, keyboardWasShown, keyboardVisible, appState]);

    useKeyboardCloseHandler(visible ? onClose : undefined);

    if (modal) {
        return (
            <Modal
                {...modalProps}
                style={[styles.modal, style]}
                isVisible={visible}
                deviceHeight={screenHeight}
                onModalShow={onShow}
                onModalHide={onHide}
                {...(!isWeb && { onBackButtonPress: onClose })}
                animationOutTiming={1}
            >
                <PinDialogView
                    bodyRef={bodyRef}
                    appState={appState}
                    status={status}
                    haveToShowCloseButton={haveToShowCloseButton}
                    onSubmit={onSubmit}
                    onBodyPress={handleBodyPress}
                    onClose={handleClosePinDialog}
                />
                <VitalErrorDialog />
            </Modal>
        );
    }

    return (
        <PinDialogView
            bodyRef={bodyRef}
            appState={appState}
            status={status}
            haveToShowCloseButton={haveToShowCloseButton}
            onSubmit={onSubmit}
            onBodyPress={handleBodyPress}
            onClose={handleClosePinDialog}
        />
    );
}

export default forwardRef(PinDialog);
